import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Auction } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * AuctionLinks component
 * @returns {JSX.Element}
 */
const AuctionLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedAuctions, setPaginationRelatedAuctions] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedAuction",
    getPaginationRelatedAuctions.pageSize,
    getPaginationRelatedAuctions.page * getPaginationRelatedAuctions.pageSize,
  );

  const { data: countRelatedAuctions } = useCountRelatedFields(id, type, [
    "relatedAuction",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedAuction",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedAuction",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [searchContent, setSearchContent] = useState("");

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const handleCreateRelation = async (idToLink: string) => {
    try {
      await createRelatedFields([id, idToLink]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRelation = async (photographyId: string) => {
    await deleteRelation(photographyId);
  };

  const columns: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"auction"}
            />
          );
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="auction"
                  elements={[
                    {
                      label: t("ventesDescriptionNumeroVente"),
                      value: row.original.ventesDescriptionNumeroVente,
                      type: "text",
                    },
                    {
                      label: t("ventesDescriptionResume"),
                      value: row.original.ventesDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="auction"
                elements={[
                  {
                    label: t("ventesDescriptionNumeroVente"),
                    value: row.original.ventesDescriptionNumeroVente,
                    type: "text",
                  },
                  {
                    label: t("ventesDescriptionResume"),
                    value: row.original.ventesDescriptionResume,
                    type: "richtext",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={handleAddMode}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedPublicAuctions")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
            {/* <Button variant="default" onClick={handleAddMode}>
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("newAuction")}
            </Button> */}
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode
            ? (notAlreadyRelatedAuctions?.data ?? [])
            : (relatedFields ?? [])
        }
        pagination={
          !isAddMode
            ? {
                pagination: getPaginationRelatedAuctions,
                setPage: setPaginationRelatedAuctions,
                totalSize: countRelatedAuctions?.relatedAuction,
              }
            : {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default AuctionLinks;
